import { Activity, Download, Search, Settings, Shield, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { useDialog } from '../components/DialogProvider';
import { InviteUserModal } from '../components/InviteUserModal';
import { useAuth } from '../contexts/AuthContext';
import { useAdminUserManagement, useAdminUsers } from '../hooks/useAdminUsers';
import { AdminUser, UserRole, adminApi } from '../lib/admin-api';
import { exportUserData } from '../lib/export-utils';

// UserRole and AdminUser are now imported from admin-api

/**
 * Role badge component with color coding
 */
function RoleBadge({ role, size = 'md' }: { role: UserRole; size?: 'sm' | 'md' | 'lg' }) {
	const colors = {
		[UserRole.VIEWER]: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
		[UserRole.READ]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
		[UserRole.EXECUTE]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
		[UserRole.WRITE]: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
		[UserRole.SUPPORT]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
		[UserRole.ADMIN]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
	};

	const sizes = {
		sm: 'px-2 py-1 text-xs',
		md: 'px-3 py-1 text-sm',
		lg: 'px-4 py-2 text-base',
	};

	return (
		<span
			className={`inline-flex items-center font-medium rounded-full ${colors[role]} ${sizes[size]}`}
		>
			{role}
		</span>
	);
}

/**
 * User avatar component
 */
function UserAvatar({ user, size = 'md' }: { user: AdminUser; size?: 'sm' | 'md' | 'lg' }) {
	const sizes = {
		sm: 'w-8 h-8 text-sm',
		md: 'w-10 h-10 text-base',
		lg: 'w-12 h-12 text-lg',
	};

	if (user.picture) {
		return <img src={user.picture} alt={user.name} className={`rounded-full ${sizes[size]}`} />;
	}

	// Fallback to initials
	const initials = user.name
		.split(' ')
		.map(n => n[0])
		.join('')
		.toUpperCase()
		.slice(0, 2);

	return (
		<div
			className={`flex justify-center items-center font-medium text-white bg-blue-500 rounded-full ${sizes[size]}`}
		>
			{initials}
		</div>
	);
}

/**
 * User table row component
 */
function UserTableRow({
	user,
	onRoleChange,
	onViewDetails,
	onDeactivate,
}: {
	user: AdminUser;
	onRoleChange: (userId: string, newRole: UserRole) => void;
	onViewDetails: (user: AdminUser) => void;
	onDeactivate: (userId: string) => void;
}) {
	const [isChangingRole, setIsChangingRole] = useState(false);

	const handleRoleChange = async (newRole: UserRole) => {
		setIsChangingRole(true);
		try {
			await onRoleChange(user.id, newRole);
		} finally {
			setIsChangingRole(false);
		}
	};

	const formatLastLogin = (date?: Date | string) => {
		if (!date) return 'Never';

		// Handle both Date objects and ISO strings
		const dateObj = typeof date === 'string' ? new Date(date) : date;
		if (isNaN(dateObj.getTime())) return 'Invalid date';

		const now = new Date();
		const diffHours = (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60);

		if (diffHours < 1) return 'Just now';
		if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
		if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;
		return dateObj.toLocaleDateString();
	};

	return (
		<tr className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800">
			{/* User Info */}
			<td className="px-6 py-4 whitespace-nowrap">
				<div className="flex items-center space-x-3">
					<UserAvatar user={user} />
					<div>
						<div className="text-sm font-medium text-gray-900 dark:text-white">
							{user.name}
						</div>
						<div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
					</div>
				</div>
			</td>

			{/* Roles */}
			<td className="px-6 py-4 whitespace-nowrap">
				<div className="flex flex-wrap gap-1">
					{user.roles.map(role => (
						<RoleBadge key={role} role={role} size="sm" />
					))}
				</div>
			</td>

			{/* Status */}
			<td className="px-6 py-4 whitespace-nowrap">
				<span
					className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
						user.isActive
							? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
							: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
					}`}
				>
					{user.isActive ? 'Active' : 'Inactive'}
				</span>
			</td>

			{/* Last Login */}
			<td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-400">
				{formatLastLogin(user.lastLoginAt)}
			</td>

			{/* Actions */}
			<td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
				<div className="flex justify-end items-center space-x-2">
					{/* Role Change Dropdown */}
					<select
						value={user.roles[0] || UserRole.READ}
						onChange={e => handleRoleChange(e.target.value as UserRole)}
						disabled={isChangingRole}
						className="px-2 py-1 text-sm bg-white rounded-md border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
					>
						{Object.values(UserRole).map(role => (
							<option key={role} value={role}>
								{role}
							</option>
						))}
					</select>

					{/* View Details Button */}
					<button
						onClick={() => onViewDetails(user)}
						className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
					>
						View
					</button>

					{/* Deactivate Button (if not current user) */}
					{user.isActive && (
						<button
							onClick={() => onDeactivate(user.id)}
							className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
						>
							Deactivate
						</button>
					)}
				</div>
			</td>
		</tr>
	);
}

/**
 * Admin Users Page - Sophisticated user management interface
 */
export function AdminUsersPage() {
	const { user: currentUser } = useAuth();
	const { alert, confirm } = useDialog();
	const [searchTerm, setSearchTerm] = useState('');
	const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
	const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
	const [showInviteModal, setShowInviteModal] = useState(false);

	// API hooks - using real backend API
	const {
		data: usersData,
		isLoading: loading,
		error,
	} = useAdminUsers({
		search: searchTerm || undefined,
		role: roleFilter !== 'all' ? roleFilter : undefined,
		isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
	});

	// Fallback to mock data if API fails
	const mockUsersData = {
		users: [
			{
				id: '1',
				email: 'josh@vquip.com',
				name: 'Josh Gonzalez',
				picture: undefined,
				roles: [UserRole.ADMIN],
				isActive: true,
				lastLoginAt: new Date(),
				createdAt: new Date('2024-01-01'),
			},
		],
		total: 1,
		page: 1,
		limit: 20,
		pages: 1,
	};

	// Use real API data (usersData from useAdminUsers hook above)

	// Temporarily disable statistics until backend endpoint is ready
	// const { data: statistics } = useAdminStatistics();
	const statistics = null;
	// Enable real admin management hooks
	const { updateUserRoles, deactivateUser, isUpdatingRoles, isDeactivating } =
		useAdminUserManagement();

	const users = usersData?.users || mockUsersData.users;

	const handleRoleChange = async (userId: string, newRole: UserRole) => {
		try {
			await updateUserRoles({
				userId,
				data: {
					roles: [newRole],
					reason: `Role changed via admin interface`,
				},
			});
		} catch (error) {
			console.error('Failed to update user role:', error);
			alert({
				title: 'Error',
				message: 'Failed to update user role. Please try again.',
				type: 'error',
			});
		}
	};

	const handleViewDetails = (user: AdminUser) => {
		// Show user details in a styled dialog
		alert({
			title: `User Details - ${user.name}`,
			message: `
Email: ${user.email}
Role: ${user.roles.join(', ')}
Status: ${user.isActive ? 'Active' : 'Inactive'}
Created: ${new Date(user.createdAt).toLocaleDateString()}
Last Login: ${user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
			`.trim(),
			type: 'info',
		});
	};

	const handleDeactivate = async (userId: string) => {
		// Find the user to get their name for confirmation
		const user = users.find(u => u.id === userId);
		if (!user) return;

		// Show confirmation dialog
		confirm({
			title: 'Deactivate User',
			message: `Are you sure you want to deactivate ${user.name} (${user.email})?\n\nThis will prevent them from accessing the system.`,
			confirmText: 'Deactivate',
			cancelText: 'Cancel',
			type: 'danger',
			onConfirm: async () => {
				try {
					await deactivateUser({
						userId,
						reason: 'Deactivated via admin interface',
					});

					alert({
						title: 'Success',
						message: `${user.name} has been deactivated successfully.`,
						type: 'success',
					});
				} catch (error) {
					console.error('Failed to deactivate user:', error);
					alert({
						title: 'Error',
						message: 'Failed to deactivate user. Please try again.',
						type: 'error',
					});
				}
			},
		});
	};

	// Handle user invitation
	const handleInviteUser = async (invitation: {
		email: string;
		name: string;
		role: string;
		message?: string;
	}) => {
		try {
			// Call the real API
			const result = await adminApi.inviteUser(invitation);

			// Show success dialog
			alert({
				title: 'Invitation Sent',
				message: result.message || `Invitation sent successfully to ${invitation.email}`,
				type: 'success',
			});
		} catch (error) {
			console.error('Failed to invite user:', error);
			throw error;
		}
	};

	const filteredUsers = users.filter(user => {
		const matchesSearch =
			!searchTerm ||
			user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.name.toLowerCase().includes(searchTerm.toLowerCase());

		const matchesRole = roleFilter === 'all' || user.roles.includes(roleFilter);

		const matchesStatus =
			statusFilter === 'all' ||
			(statusFilter === 'active' && user.isActive) ||
			(statusFilter === 'inactive' && !user.isActive);

		return matchesSearch && matchesRole && matchesStatus;
	});

	// Calculate role distribution from actual user data
	const roleDistribution = users.reduce((acc, user) => {
		// Handle both array and single role formats
		const userRoles = Array.isArray(user.roles) ? user.roles : [user.roles];
		userRoles.forEach(role => {
			acc[role] = (acc[role] || 0) + 1;
		});
		return acc;
	}, {} as Record<string, number>);

	if (loading) {
		return (
			<div className="flex justify-center items-center min-h-screen">
				<div className="text-center">
					<div className="mx-auto w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
					<p className="mt-4 text-gray-600 dark:text-gray-400">Loading users...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="p-8 mx-auto max-w-7xl">
			{/* Header */}
			<div className="mb-8">
				<div className="flex justify-between items-center">
					<div>
						<h1 className="flex items-center text-3xl font-bold text-gray-900 dark:text-white">
							<Users className="mr-3 w-8 h-8 text-blue-600" />
							User Management
						</h1>
						<p className="mt-2 text-gray-600 dark:text-gray-400">
							Manage user roles and permissions for your workflow engine
						</p>
					</div>

					<div className="flex items-center space-x-3">
						<button
							onClick={() => exportUserData(filteredUsers)}
							className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
						>
							<Download className="mr-2 w-4 h-4" />
							Export Users
						</button>
						<button
							onClick={() => setShowInviteModal(true)}
							className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700"
						>
							<UserPlus className="mr-2 w-4 h-4" />
							Invite User
						</button>
					</div>
				</div>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-4">
				<div className="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<Users className="w-6 h-6 text-gray-400" />
							</div>
							<div className="flex-1 ml-5 w-0">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate dark:text-gray-400">
										Total Users
									</dt>
									<dd className="text-lg font-medium text-gray-900 dark:text-white">
										{users.length}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>

				<div className="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<Activity className="w-6 h-6 text-green-400" />
							</div>
							<div className="flex-1 ml-5 w-0">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate dark:text-gray-400">
										Active Users
									</dt>
									<dd className="text-lg font-medium text-gray-900 dark:text-white">
										{users.filter(u => u.isActive).length}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>

				<div className="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<Shield className="w-6 h-6 text-red-400" />
							</div>
							<div className="flex-1 ml-5 w-0">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate dark:text-gray-400">
										Admin Users
									</dt>
									<dd className="text-lg font-medium text-gray-900 dark:text-white">
										{roleDistribution[UserRole.ADMIN] || 0}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>

				<div className="overflow-hidden bg-white rounded-lg shadow dark:bg-gray-800">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<Settings className="w-6 h-6 text-purple-400" />
							</div>
							<div className="flex-1 ml-5 w-0">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate dark:text-gray-400">
										Write Users
									</dt>
									<dd className="text-lg font-medium text-gray-900 dark:text-white">
										{roleDistribution[UserRole.WRITE] || 0}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="mb-6 bg-white rounded-lg shadow dark:bg-gray-800">
				<div className="p-6">
					<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
						{/* Search */}
						<div className="relative">
							<div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
								<Search className="w-5 h-5 text-gray-400" />
							</div>
							<input
								type="text"
								placeholder="Search users..."
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								className="block py-2 pr-3 pl-10 w-full leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
							/>
						</div>

						{/* Role Filter */}
						<div>
							<select
								value={roleFilter}
								onChange={e => setRoleFilter(e.target.value as UserRole | 'all')}
								className="block px-3 py-2 w-full bg-white rounded-md border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
							>
								<option value="all">All Roles</option>
								{Object.values(UserRole).map(role => (
									<option key={role} value={role}>
										{role} ({roleDistribution[role] || 0})
									</option>
								))}
							</select>
						</div>

						{/* Status Filter */}
						<div>
							<select
								value={statusFilter}
								onChange={e =>
									setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
								}
								className="block px-3 py-2 w-full bg-white rounded-md border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
							>
								<option value="all">All Status</option>
								<option value="active">
									Active ({users.filter(u => u.isActive).length})
								</option>
								<option value="inactive">
									Inactive ({users.filter(u => !u.isActive).length})
								</option>
							</select>
						</div>
					</div>
				</div>
			</div>

			{/* Users Table */}
			<div className="overflow-hidden bg-white shadow dark:bg-gray-800 sm:rounded-md">
				<div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
					<h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
						Users ({filteredUsers.length})
					</h3>
				</div>

				{filteredUsers.length === 0 ? (
					<div className="py-12 text-center">
						<Users className="mx-auto w-12 h-12 text-gray-400" />
						<h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
							No users found
						</h3>
						<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
							{searchTerm
								? 'Try adjusting your search or filters.'
								: 'Get started by inviting a user.'}
						</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
							<thead className="bg-gray-50 dark:bg-gray-900">
								<tr>
									<th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-400">
										User
									</th>
									<th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-400">
										Roles
									</th>
									<th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-400">
										Status
									</th>
									<th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-400">
										Last Login
									</th>
									<th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase dark:text-gray-400">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
								{filteredUsers.map(user => (
									<UserTableRow
										key={user.id}
										user={user}
										onRoleChange={handleRoleChange}
										onViewDetails={handleViewDetails}
										onDeactivate={handleDeactivate}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Role Distribution Chart */}
			<div className="mt-8 bg-white rounded-lg shadow dark:bg-gray-800">
				<div className="p-6">
					<h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
						Role Distribution
					</h3>
					<div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
						{Object.values(UserRole).map(role => (
							<div key={role} className="text-center">
								<div className="text-2xl font-bold text-gray-900 dark:text-white">
									{roleDistribution[role] || 0}
								</div>
								<div className="text-sm text-gray-500 dark:text-gray-400">
									<RoleBadge role={role} size="sm" />
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Invite User Modal */}
			<InviteUserModal
				isOpen={showInviteModal}
				onClose={() => setShowInviteModal(false)}
				onInvite={handleInviteUser}
			/>
		</div>
	);
}
